import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Messages, Category } from '@/common/constants';
import Loading from '@/components/common/Loading';
import URL from '@/common/routes/Url';
import CommonActionButton from '@/components/common/CommonActionButton';
import MyGroupService from '@/services/MyGroup.service';
import { MyGroup } from '@/types/MyGroup.type';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import useSidebar from '@/common/hooks/useSidebar';

const MyGroupDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<MyGroup>({ shouldUnregister: false });
  const {
    handleSubmit,
    formState: { isSubmitting },
    getValues,
    reset,
    control,
  } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { reload } = useSidebar();
  const [loading, setLoading] = useState(true);

  const explanatoryNotes = {
    プライベート: '自分のみ編集・閲覧が可能です。',
    共有: 'メンバーに設定した人に共有され、閲覧可能になります。',
    パブリック: '全社員が閲覧可能です。※追加要望は管理者まで',
  } as const;

  const name: HookFormControlsProps = {
    name: 'name',
    type: 'text',
    label: 'グループ名',
    rule: {
      required: 'グループ名は必須項目です。',
      maxLength: {
        value: 40,
        message: 'グループ名は40文字以内で入力してください。',
      },
    },
  };
  // const category: HookFormControlsProps = {
  //   name: 'category',
  //   type: 'select',
  //   label: 'グループの種類',
  //   options: Category,
  //   rule: {
  //     required: 'グループの種類は必須項目です。',
  //   },
  // };
  const memo: HookFormControlsProps = {
    name: 'memo',
    type: 'text',
    label: 'グループの説明',
    rule: {
      maxLength: {
        value: 200,
        message: 'グループの説明は200文字以内で入力してください。',
      },
    },
  };

  useLayoutEffect(() => {
    (async () => {
      if (!pathParams.id) {
        setLoading(false);
        return;
      }
      await MyGroupService.get(pathParams.id)
        .then((response: { data: MyGroup }) => {
          reset(response.data);
        })
        .catch((_e) => {
          setLoading(false);
          enqueueSnackbar(Messages.select.failure);
        });
      setLoading(false);
    })();
  }, []);

  const onSubmit = async (values: MyGroup) => {
    try {
      values.category = Category.プライベート;
      if (pathParams.id) {
        await MyGroupService.update(values, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await MyGroupService.create(values);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      reload();
      navigate(URL.myGroups);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={8}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>Myグループ ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <Box sx={{ marginBottom: '4rem' }}>
            <Box sx={{ display: 'flex' }}>
              <Box
                style={{
                  fontSize: 'x-large',
                  marginLeft: '10px',
                  marginTop: '10px',
                }}
              >
                {pathParams.id ? getValues('name') : '新規グループ'} | プロパティ
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`${URL.employeeMyGroups}/${getValues('id')}`)}
                size="small"
                sx={{
                  marginLeft: 'auto',
                  marginTop: '10px',
                  marginRight: '40px',
                  display: pathParams.id ?? 'none',
                }}
              >
                メンバー編集へ
              </Button>
            </Box>
          </Box>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Table aria-label="my group table">
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" width={'150'}>
                        {'グループ名 *'}
                      </TableCell>
                      <TableCell align="left">
                        <HookFormControls {...name} />
                      </TableCell>
                    </TableRow>
                    {/* <TableRow>
                      <TableCell align="center" width={'150'}>
                        {'グループの種類 *'}
                      </TableCell>
                      <TableCell align="left">
                        <Controller
                          name={category.name as 'category'}
                          control={control}
                          rules={category.rule}
                          defaultValue={undefined}
                          render={({ field, fieldState }) => (
                            <FormControl fullWidth error={!!fieldState.error}>
                              <InputLabel id={category.name}>{category.label}</InputLabel>
                              <Select fullWidth labelId={category.name} label={category.label} {...field}>
                                {Object.keys(category.options).map((key) => (
                                  <MenuItem
                                    key={category.options[key as keyof typeof category.options]}
                                    value={category.options[key as keyof typeof category.options]}
                                  >
                                    {key}__
                                    <Box component="span" sx={{ fontSize: 13, color: '#1272D1' }}>
                                      {explanatoryNotes[key as keyof typeof explanatoryNotes]}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>{fieldState.error?.message}</FormHelperText>
                            </FormControl>
                          )}
                        />
                      </TableCell>
                    </TableRow> */}
                    <TableRow>
                      <TableCell align="center" width={'150'}>
                        {'グループの説明'}
                      </TableCell>
                      <TableCell align="left">
                        <HookFormControls {...memo} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Grid item xs={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default MyGroupDetail;
